import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';

import RatingV5 from './RatingV5';
import { getRatingDetails, setRatingChoice } from '../../../utils';
import { utrTypeIds } from '../../../utils/eventConstants';
import useRatignProps from '../../hooks/useRatingProps';
import { ratingTypes, gameTypes } from '../../../utils/constants';

const TennisRating = props => {
    const {
        auth,
        profileData,
        isOwnProfile,
        onTennisGetRatedClick,
        doNotMask = false,
        'data-testid': dataTestId,
    } = props;

    const userLoggedIn = auth?.user;
    const { utrRangeRequired } = userLoggedIn || {};

    const historicRatingData = profileData?.historicRatings;

    const ratingNames = {
        singles: {
            verified: 'singlesUtr',
            verifiedStatus: 'ratingStatusSingles',
            nonVerified: 'myUtrSingles',
            nonVerifiedStatus: 'myUtrStatusSingles',
            historic: 'historicSinglesRating',
            historicDisplay: 'historicSinglesRatingDisplay',
            historicDate: 'historicSinglesRatingDate',
            progress: 'ratingProgressSingles',

        },
        doubles: {
            verified: 'doublesUtr',
            verifiedStatus: 'ratingStatusDoubles',
            nonVerified: 'myUtrDoubles',
            nonVerifiedStatus: 'myUtrStatusDoubles',
            historic: 'historicDoublesRating',
            historicDisplay: 'historicDoublesRatingDisplay',
            historicDate: 'historicDoublesRatingDate',
            progress: 'ratingProgressDoubles',
        },
    };

    const {
        hasVerifiedSinglesRating,
        hasUnverifiedSinglesRating,
        hasHistoricSinglesRating,
        hasVerifiedDoublesRating,
        hasUnverifiedDoublesRating,
        hasHistoricDoublesRating,
        hasEstimatedRange,
    } = useRatignProps(profileData);

    const hasSinglesRating = hasVerifiedSinglesRating || hasUnverifiedSinglesRating || hasHistoricSinglesRating;
    const hasDoublesRating = hasVerifiedDoublesRating || hasUnverifiedDoublesRating || hasHistoricDoublesRating;
    const showGetRatedButton = !hasSinglesRating && !hasDoublesRating && !hasEstimatedRange && isOwnProfile;

    // TODO: This should be memoized
    const getUtrRating = (gameType) => {
        const ratingProps = {
            titleColor: 'blue',
            title: ratingTypes.tennis,
            value: '',
            footer: '',
            isVerified: false,
            showRatingInfo: true,
            gameType,
        };
        let rating;

        // 1. show verified rating if available
        if ((gameType === gameTypes.SINGLES && hasVerifiedSinglesRating) || (gameType === gameTypes.DOUBLES && hasVerifiedDoublesRating)) {
            rating = getRatingDetails(setRatingChoice({ player: profileData, ratingType: gameType, utrType: utrTypeIds.SANCTIONED }));

            // 2. show unverified rating if available
        } else if ((gameType === gameTypes.SINGLES && hasUnverifiedSinglesRating) || (gameType === gameTypes.DOUBLES && hasUnverifiedDoublesRating)) {
            rating = getRatingDetails(setRatingChoice({ player: profileData, ratingType: gameType, utrType: utrTypeIds.MYUTR }));
        }

        if (rating) {
            // eslint-disable-next-line prefer-template
            ratingProps.value = `${rating.ratingNumber}${rating.ratingDecimal.length > 0 ? '.' + rating.ratingDecimal : ''}`;
            ratingProps.footer = rating.progress ? `${rating.progress}% Reliable` : '';
            ratingProps.isVerified = rating.verified;
            ratingProps.showRatingInfo = true;

            // 3. show historic rating if available
        } else if (historicRatingData?.[ratingNames[gameType].historic] != null) {
            ratingProps.value = historicRatingData?.[ratingNames[gameType].historicDisplay];
            ratingProps.footer = `From ${moment(historicRatingData?.[ratingNames[gameType].historicDate]).format('MMMM YYYY')}`;
            ratingProps.isVerified = true;

        // singles display fallback cases
        } else if (gameType === gameTypes.SINGLES) {
            // 4. show estimated range if available
            if (hasEstimatedRange) {
                ratingProps.value = `${doNotMask ? profileData.utrRange.minUtr?.toFixed(2) : profileData.utrRange.minUtrDisplay} - ${doNotMask ? profileData.utrRange.maxUtr?.toFixed(2) : profileData.utrRange.maxUtrDisplay}`;
                ratingProps.footer = profileData[ratingNames[gameType].progress] ? `${profileData[ratingNames[gameType].progress]}% Reliable` : 'Estimated';
                ratingProps.showRatingInfo = true;
                ratingProps.gameType = null;
            // 5. show Get Rated button
            } else if (showGetRatedButton && utrRangeRequired) {
                ratingProps.title = 'UTR-Tennis';
                ratingProps.showGetRatedButton = true;
                ratingProps.onGetRatedClick = onTennisGetRatedClick;
                ratingProps.gameType = null;
            // 6. show Unrated (UR)
            } else {
                ratingProps.value = '';
                ratingProps.footer = '';
                ratingProps.isVerified = false;
                ratingProps.showRatingInfo = true;
                ratingProps.gameType = null;
            }
        }

        return (
            <RatingV5
                {...ratingProps}
                data-testid={dataTestId ? `${dataTestId}.${gameType || 'singles'}` : undefined}
            />
        );
    };

    return (
        <>
            {(hasSinglesRating || !hasDoublesRating) && getUtrRating(gameTypes.SINGLES)}
            {hasDoublesRating && getUtrRating(gameTypes.DOUBLES)}
        </>
    );
};

TennisRating.defaultProps = {
    isOwnProfile: false,
    doNotMask: false,
};

TennisRating.propTypes = {
    auth: PropTypes.instanceOf(Object).isRequired,
    profileData: PropTypes.instanceOf(Object).isRequired,
    isOwnProfile: PropTypes.bool,
    onTennisGetRatedClick: PropTypes.func.isRequired,
    doNotMask: PropTypes.bool,
};

export default TennisRating;
